import type { MentorshipAlertGroup, RiskFlag } from "@/lib/api/risk-flagging";

/** Group header row shown for each mentorship+tier */
export interface GroupHeaderRow {
  _type: "group";
  group: MentorshipAlertGroup;
}

/** Individual alert row shown when a group is expanded */
export interface AlertChildRow {
  _type: "child";
  alert: RiskFlag;
}

/** Union type for all rows in the risk flagging table */
export type RiskFlagRow = GroupHeaderRow | AlertChildRow;

/**
 * Flatten mentorship+tier groups into a row array.
 * Group headers always appear; child rows only appear when expanded.
 * expandedGroups is keyed by group.groupKey ("{mentorshipId}::{priority}").
 */
const PRIORITY_RANK: Record<string, number> = { HIGH: 0, MEDIUM: 1, STANDARD: 2 };

export function flattenGroups(
  groups: MentorshipAlertGroup[],
  expandedGroups: Set<string>,
): RiskFlagRow[] {
  const items: { sortDate: number; priorityRank: number; group: MentorshipAlertGroup }[] = [];
  for (const group of groups) {
    if (group.latestAlert.sourceType === "survey") {
      for (const alert of group.alerts) {
        items.push({
          sortDate: new Date(alert.created_at).getTime(),
          priorityRank: PRIORITY_RANK[alert.priority] ?? 99,
          group: {
            ...group,
            alerts: [alert],
            alertCount: 1,
            latestAlert: alert,
          },
        });
      }
      continue;
    }
    items.push({
      sortDate: new Date(group.latestAlert.created_at).getTime(),
      priorityRank: PRIORITY_RANK[group.priority] ?? 99,
      group,
    });
  }

  items.sort(
    (a, b) => a.priorityRank - b.priorityRank || b.sortDate - a.sortDate,
  );

  const rows: RiskFlagRow[] = [];
  for (const { group } of items) {
    rows.push({ _type: "group", group });
    if (group.alertCount > 1 && expandedGroups.has(group.groupKey)) {
      for (const alert of group.alerts) {
        rows.push({ _type: "child", alert });
      }
    }
  }
  return rows;
}
